import type { FormEventHandler } from 'react'
import { useTranslation } from 'react-i18next'
import NextLink from 'next/link'
import type { Control } from 'react-hook-form'
import { Stack, Typography } from '@mui/material'
import { Button } from 'settle-ui/components/Button/Button'
import { Section } from 'settle-ui/components/Section/Section'
import { SectionItem } from 'settle-ui/components/SectionItem/SectionItem'
import { TextFieldController } from 'settle-ui/form/TextFieldController/TextFieldController'
import { AuthCard } from 'routes/components/AuthCard/AuthCard'
import { RouterPage } from 'lib/navigation/consts'
import { EMAIL_PATTERN } from 'lib/patterns'

export interface ResetPasswordRequestFormValues {
  email: string
}

export interface ResetPasswordRequestCardProps {
  control: Control<ResetPasswordRequestFormValues>
  isLoading: boolean
  onSubmit: FormEventHandler<HTMLFormElement>
}

/**
 * Email-entry step of the password reset flow. Owns no state — the caller holds the form and decides
 * when to swap this card for the confirmation one. Back is a link rather than a button so it cannot
 * submit the form it sits inside.
 */
export const ResetPasswordRequestCard: Settle.FC<ResetPasswordRequestCardProps> = ({
  control,
  isLoading,
  onSubmit,
}) => {
  const { t } = useTranslation()

  return (
    <AuthCard fullWidth={true}>
      <Section fullWidth={true}>
        <SectionItem fullWidth={true} horizontalGutter="none" verticalGutter="none" gap={1}>
          <Typography variant="h5" fontWeight={700} textAlign="center" color="onSurfacePrimary">
            {t('enterEmailToResetPassword')}
          </Typography>
          <Typography variant="body1" color="onSurfaceSecondary" textAlign="center">
            {t('forgotPasswordPage::subtitle')}
          </Typography>
        </SectionItem>
      </Section>
      <Stack component="form" noValidate={true} onSubmit={onSubmit} sx={{ flex: 1 }}>
        <Section fullWidth={true} sx={{ my: 2 }}>
          <SectionItem fullWidth={true} horizontalGutter="none">
            <TextFieldController
              margin="none"
              name="email"
              type="email"
              control={control}
              label={t('label::input::email')}
              autoComplete="username"
              rules={{
                pattern: { value: EMAIL_PATTERN, message: t('emailIsIncorrect') },
                required: { value: true, message: t('emailCantBeEmpty') },
              }}
            />
          </SectionItem>
        </Section>
        <Section fullWidth={true} sx={{ marginTop: 'auto', pb: 0 }}>
          <SectionItem fullWidth={true} horizontalGutter="none" direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Button component={NextLink} href={RouterPage.login} variant="secondary" fullWidth={true}>
              {t('back')}
            </Button>
            <Button type="submit" variant="primary" fullWidth={true} loading={isLoading}>
              {t('confirm')}
            </Button>
          </SectionItem>
        </Section>
      </Stack>
    </AuthCard>
  )
}
